import CryptoJS from "crypto-js";

// Cache server public key
let cachedPublicPem: string | null = null;
let publicPemFetchedAt = 0;
const PUBLIC_KEY_TTL_MS = 1000 * 60 * 5;

export async function fetchPublicKey(): Promise<string> {
    const now = Date.now();
    if (cachedPublicPem && now - publicPemFetchedAt < PUBLIC_KEY_TTL_MS) return cachedPublicPem;

    const resp: any = await $apiServer('/security/public-key', { method: 'POST' });

    if (!resp || !resp.data) throw new Error("Failed to fetch public key");
    
    const pem = resp.data;

    cachedPublicPem = pem;
    publicPemFetchedAt = Date.now();

    return pem;
}


function pemToArrayBuffer(pem: string) {
    const b64 = pem.replace(/-----BEGIN PUBLIC KEY-----/, "")
        .replace(/-----END PUBLIC KEY-----/, "")
        .replace(/\s+/g, "");
    const raw = atob(b64);
    const u8 = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) u8[i] = raw.charCodeAt(i);
    return u8.buffer;
}

function arrayBufferToBase64(buf: ArrayBuffer) {
    const bytes = new Uint8Array(buf);
    let binary = "";
    for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
}

/** Encrypt request payload */
export async function encryptPayload(payloadObj: any) {

  if (!process.client) throw new Error("encryptPayload can only run on client-side");

  const publicPem = await fetchPublicKey();

  const aesRaw = window.crypto.getRandomValues(new Uint8Array(32));
  const iv = window.crypto.getRandomValues(new Uint8Array(16));

  const rsaPubKey = await window.crypto.subtle.importKey(
    "spki",
    pemToArrayBuffer(publicPem),
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["encrypt"]
  );

  const aesInfoJson = JSON.stringify({
    key: btoa(String.fromCharCode(...aesRaw)),
    iv: btoa(String.fromCharCode(...iv)),
  });

  const wrapped = await window.crypto.subtle.encrypt(
    { name: "RSA-OAEP" },
    rsaPubKey,
    new TextEncoder().encode(aesInfoJson)
  );

  const wrappedB64 = arrayBufferToBase64(wrapped);

  const keyWord = CryptoJS.lib.WordArray.create(aesRaw as any);
  const ivWord = CryptoJS.lib.WordArray.create(iv as any);

  let plaintext: string;

  if (payloadObj instanceof FormData) {
    // Convert FormData to an object first
    const obj: Record<string, any> = {};
    payloadObj.forEach((value, key) => {
      if (value instanceof File) {
        // Handle File separately (convert to base64)
        obj[key] = value; // you can keep File as-is and handle on backend if needed
      } else {
        obj[key] = value;
      }
    });
    plaintext = JSON.stringify(obj);
  } else if (typeof payloadObj === "string") {
    plaintext = payloadObj;
  } else {
    plaintext = JSON.stringify(payloadObj);
  }

  const encrypted = CryptoJS.AES.encrypt(plaintext, keyWord, {
    iv: ivWord,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });

  return {
    secret: wrappedB64,
    data: encrypted.ciphertext.toString(CryptoJS.enc.Base64),
    _clientAesRawBase64: btoa(String.fromCharCode(...aesRaw)),
    _clientIvBase64: btoa(String.fromCharCode(...iv)),
  };
}



/** Decrypt backend response */
export function decryptPayload(aesRawBase64: string, ivBase64: string, dataBase64: string) {
    const aesRaw = Uint8Array.from(atob(aesRawBase64), c => c.charCodeAt(0));
    const iv = Uint8Array.from(atob(ivBase64), c => c.charCodeAt(0));

    const keyWord = CryptoJS.lib.WordArray.create(aesRaw as any);
    const ivWord = CryptoJS.lib.WordArray.create(iv as any);

    const decrypted = CryptoJS.AES.decrypt(
        { ciphertext: CryptoJS.enc.Base64.parse(dataBase64) },
        keyWord,
        { iv: ivWord, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
    );

    const text = decrypted.toString(CryptoJS.enc.Utf8);
    try { return JSON.parse(text); } catch { return text; }
}

